import React from "react";
import { Box } from "braid-design-system";
import Loading from "../components/Loading";
import PromotionListTable from "../components/Promotion/PromotionListTable";
import CreateUpdatePromotionDrawer from "../components/Promotion/CreateUpdatePromotionDrawer";
import {
    PromotionToEdit,
} from "../components/Promotion/types";
import {
    createPromotion,
    getAllPromotions,
    updatePromotion,
    deletePromotion,
    PromoHedResponse,
    PromoHedRequest,
} from "../services/promotionService";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";

const PromotionManagement: React.FC = () => {
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [promotionList, setPromotionList] = React.useState<PromoHedResponse[]>([]);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedPromotion, setSelectedPromotion] = React.useState<PromotionToEdit | undefined>(undefined);

    const fetchPromotions = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage("Please wait, Fetching promotions...");
            const data = await getAllPromotions();
            setPromotionList(data);
        } catch (error: any) {
            toast.showError(`Error fetching promotions: ${error?.message || "Unknown error"}`);
            setPromotionList([]);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [toast]);

    React.useEffect(() => {
        fetchPromotions();
    }, [fetchPromotions]);

    const handleSavePromotion = React.useCallback(
        async (payload: PromoHedRequest) => {
            try {
                if (selectedPromotion?.promotion_id) {
                    await updatePromotion(selectedPromotion.promotion_id, payload);
                    toast.showSuccess('Promotion updated successfully');
                } else {
                    await createPromotion(payload);
                    toast.showSuccess('Promotion created successfully');
                }
            } catch (error: any) {
                const errorMsg = error?.message || (selectedPromotion?.promotion_id ? 'Failed to update promotion' : 'Failed to create promotion');
                toast.showError(errorMsg);
                throw error; // Re-throw so the drawer knows to keep saving state
            }
        },
        [selectedPromotion, toast],
    );

    const handleEditPromotion = React.useCallback((promotion: PromoHedResponse) => {
        setSelectedPromotion({
            promo_code: promotion.promo_code,
            promo_name: promotion.promo_name,
            promo_desc: promotion.promo_desc || '',
            promo_type_id: (promotion as any).promo_type_id ?? null,
            promo_datefrom: promotion.promo_datefrom || '',
            promo_dateto: promotion.promo_dateto || '',
            promo_timefrom: promotion.promo_timefrom || '',
            promo_timeto: promotion.promo_timeto || '',
            promo_days_mask: promotion.promo_days ?? 0,
            promo_active: promotion.promo_active,
            offer_criteria: (promotion.details || []).map((d, idx) => ({
                id: d.id,
                line_seq: d.line_seq ?? idx,
                criteria_type: d.criteria_type as any,
                criteria_value: d.criteria_value || '',
                amount_min: d.amount_min ?? null,
                amount_max: d.amount_max ?? null,
            })),
            promo_disper: promotion.promo_disper != null ? String(promotion.promo_disper) : '',
            promo_disamt: promotion.promo_disamt != null ? String(promotion.promo_disamt) : '',
            promo_free_item: promotion.promo_free_item || '',
            promo_free_voucher: promotion.promo_free_voucher || '',
            promotion_id: promotion.id,
        });
        setDrawerOpen(true);
    }, []);

    const handleDeletePromotion = React.useCallback(
        async (promotionId: number) => {
            if (!window.confirm('Are you sure you want to delete this promotion?')) {
                return;
            }
            try {
                await deletePromotion(promotionId);
                toast.showSuccess('Promotion deleted successfully');
                fetchPromotions();
            } catch (error: any) {
                toast.showError(`Error deleting promotion: ${error?.message || "Unknown error"}`);
            }
        },
        [toast, fetchPromotions],
    );

    const handleCloseDrawer = React.useCallback(() => {
        setDrawerOpen(false);
        setSelectedPromotion(undefined);
    }, []);

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <PromotionListTable
                    promotionList={promotionList}
                    onCreatePromotion={canManageMasters ? () => setDrawerOpen(true) : undefined}
                    onEditPromotion={handleEditPromotion}
                    onDeletePromotion={canManageMasters ? handleDeletePromotion : undefined}
                />
            </Box>

            <CreateUpdatePromotionDrawer
                isOpen={drawerOpen}
                onClose={handleCloseDrawer}
                promotionToEdit={selectedPromotion}
                onSave={handleSavePromotion}
                onPromotionSaved={fetchPromotions}
                isReadOnly={!canManageMasters}
            />
        </>
    );
};

export default PromotionManagement;